import { openDB } from "@dumbmatter/idb";
import type {
	IDBPDatabase,
	IDBPTransaction,
	StoreNames,
} from "@dumbmatter/idb";
import { logEvent } from "../util/index.ts";

// If duplicate message is sent multiple times in a row (like IndexedDB transaction abort with many open requests), only show one
const debounceMessagesStore = new Map<string, number>();
const stopBecauseDebounce = (text: string) => {
	const timeoutID = debounceMessagesStore.get(text);
	if (timeoutID === undefined) {
		const newTimeoutID = self.setTimeout(() => {
			debounceMessagesStore.delete(text);
		}, 1000);
		debounceMessagesStore.set(text, newTimeoutID);
		return false;
	}
	return true;
};

const connectIndexedDB = async <DBTypes>({
	name,
	version,
	create,
	migrate,
	lid,
}: {
	name: string;
	version: number;
	lid: number;
	create: (db: IDBPDatabase<DBTypes>) => void;
	migrate: (a: {
		db: IDBPDatabase<DBTypes>;
		lid: number;
		oldVersion: number;
		transaction: IDBPTransaction<
			DBTypes,
			StoreNames<DBTypes>[],
			"versionchange"
		>;
	}) => Promise<void>;
}) => {
	const db = await openDB<DBTypes>(name, version, {
		async upgrade(db, oldVersion, newVerison, transaction) {
			if (oldVersion === 0) {
				create(db);
			} else {
				await migrate({ db, lid, oldVersion, transaction });
			}
		},
		blocked() {
			logEvent({
				type: "error",
				text: "Please close any other open tabs.",
				textKey: "event.closeOtherTabs",
				saveToDb: false,
			});
		},
		blocking() {
			db.close();
		},
		terminated() {
			logEvent({
				type: "error",
				text: "Something bad happened. Please try restarting your browser.",
				textKey: "event.restartBrowser",
				saveToDb: false,
				persistent: true,
			});
		},
	});

	const quotaErrorMessage = `the app server cannot store any more game data.<br><br>Try <a href="/">deleting unused leagues</a> or freeing up storage on the server/device.`;

	db.addEventListener("abort", (event: any) => {
		console.error(`${name} database abort event`, event.target.error);

		let text: string | undefined;
		if (
			event.target.error &&
			event.target.error.name === "QuotaExceededError"
		) {
			text = `Your ${quotaErrorMessage}`;
		} else if (event.target.error) {
			text = `${name} database abort event: ${event.target.error.message}<br><br>Maybe your ${quotaErrorMessage}`;
		}

		if (text && !stopBecauseDebounce(text)) {
			logEvent({
				type: "error",
				text,
				saveToDb: false,
				persistent: true,
			});
		}

		if (event.target.error) {
			throw event.target.error;
		}
	});
	db.addEventListener("error", (event: any) => {
		console.error(`${name} database error event`, event.target.error);

		if (event.target.error) {
			let text: string;
			if (event.target.error.message.includes("abort")) {
				text = `${name} database error event: ${event.target.error.message}<br><br>Maybe your ${quotaErrorMessage}`;
			} else {
				text = `${name} database error event: ${event.target.error.message}`;
			}

			if (!stopBecauseDebounce(text)) {
				logEvent({
					type: "error",
					text,
					saveToDb: false,
					persistent: true,
				});
			}

			throw event.target.error;
		}
	});

	return db;
};

export default connectIndexedDB;
